import React, { useState, useEffect } from 'react';
import { Paper, TableBody, TableCell, TableHead, TableRow, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Checkbox, Box, Grid, Chip, Stack } from '@mui/material';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../utils/money';
import { format } from 'date-fns';
import ScrollTable from '../ScrollTable';


const BankReconciliation = () => {
  const [recs, setRecs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [openDlg, setOpenDlg] = useState(false);
  const [selected, setSelected] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [newRec, setNewRec] = useState({ account: '', statementDate: '', statementOpeningBalance: '', statementClosingBalance: '' });
  const { formatMoney: formatCurrency } = useCurrency();
  
  
  // fetch reconciliations/account list
  useEffect(() => {
    fetchList();
    fetchAccounts();
  }, []);
  const fetchList = async () => {
    const { data } = await axios.get('/accounting/bank-reconciliations');
    setRecs(data);
  };
  const fetchAccounts = async () => {
    const { data } = await axios.get('/accounting/accounts');
    setAccounts(data.filter(acc => acc.code.startsWith('1'))); // assets: bank/cash
  };
  // create rec
  const handleCreate = async () => {
    if (!newRec.account || !newRec.statementDate || !newRec.statementOpeningBalance || !newRec.statementClosingBalance) return;
    await axios.post('/accounting/bank-reconciliations', newRec);
    setOpenNew(false); setNewRec({ account: '', statementDate: '', statementOpeningBalance: '', statementClosingBalance: '' });
    fetchList();
  };
  const handleOpenRec = async (rec) => {
    const { data } = await axios.get(`/accounting/bank-reconciliations/${rec._id}`);
    setSelected(data); setOpenDlg(true);
  };
  // Save reviewed reconciliation (mark entries)
  const handleSaveRec = async () => {
    const res = await axios.put(`/accounting/bank-reconciliations/${selected._id}`, { entries: selected.entries, status: selected.status, notes: selected.notes, statementClosingBalance: selected.statementClosingBalance });
    setSelected(res.data);
    fetchList();
  };
  // summary balances
  const includedAmt = (selected && selected.entries)
    ? selected.entries.filter(e => e.included && e.journalEntry)
        .reduce((a, e) => {
          const lines = e.journalEntry.lines||[];
          return a + lines.reduce((s, l) => l.debit-l.credit+s, 0);
        }, 0)
    : 0;

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Bank Reconciliation</Typography>
        <Button variant="contained" color="primary" onClick={() => setOpenNew(true)}>Start New</Button>
      </Box>
      <ScrollTable size="small">
          <TableHead><TableRow>
            <TableCell>Date</TableCell><TableCell>Account</TableCell><TableCell>Opening</TableCell><TableCell>Closing</TableCell><TableCell>Status</TableCell><TableCell>Prepared By</TableCell><TableCell>Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {recs.map(rec => (
              <TableRow key={rec._id}>
                <TableCell>{format(new Date(rec.statementDate), 'yyyy-MM-dd')}</TableCell>
                <TableCell>{rec.account?.code} - {rec.account?.name}</TableCell>
                <TableCell>{formatCurrency(rec.statementOpeningBalance)}</TableCell>
                <TableCell>{formatCurrency(rec.statementClosingBalance)}</TableCell>
                <TableCell><Chip size="small" label={rec.status} color={rec.status==='completed'?'success':'info'} /></TableCell>
                <TableCell>{rec.preparedBy?.name}</TableCell>
                <TableCell><Button size="small" variant="outlined" onClick={()=>handleOpenRec(rec)}>Review</Button></TableCell>
              </TableRow>
            ))}
            {recs.length === 0 && <TableRow><TableCell colSpan={7} align="center">No reconciliations</TableCell></TableRow>}
          </TableBody>
        </ScrollTable>
      {/* Dialog for details */}
      <Dialog open={openDlg} onClose={()=>setOpenDlg(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Bank Reconciliation Details</DialogTitle>
        <DialogContent>
          {selected && (
            <>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2">Account</Typography>
                  <Typography>{selected.account?.code} - {selected.account?.name}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2">Statement Date</Typography>
                  <Typography>{format(new Date(selected.statementDate), 'yyyy-MM-dd')}</Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="subtitle2">Opening</Typography>
                  <Typography>{formatCurrency(selected.statementOpeningBalance)}</Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="subtitle2">Closing</Typography>
                  <TextField size="small" type="number" value={selected.statementClosingBalance}
                    onChange={e => setSelected(s => ({ ...s, statementClosingBalance: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant="subtitle2">Status</Typography>
                  <TextField select size="small" value={selected.status} onChange={e=>setSelected(s=>({...s,status:e.target.value}))}>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              <Grid container spacing={1} mb={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Notes</Typography>
                  <TextField fullWidth multiline minRows={2} value={selected.notes||''} onChange={e=>setSelected(s=>({...s,notes:e.target.value}))} />
                </Grid>
              </Grid>
              <Box mb={2}>
                <Typography variant="subtitle2">Summary</Typography>
                <Stack direction={{ xs: 'column', md:'row' }} gap={3}>
                  <Typography>Matched/Cleared: <b>{formatCurrency(includedAmt)}</b></Typography>
                  <Typography>Variance: <b>{formatCurrency(selected.statementClosingBalance - includedAmt)}</b></Typography>
                </Stack>
              </Box>
              <ScrollTable maxHeight={350} size="small"><TableHead><TableRow>
                  <TableCell>Included</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Memo</TableCell>
                  <TableCell>Debit</TableCell>
                  <TableCell>Credit</TableCell>
                  <TableCell>Line Count</TableCell>
                </TableRow></TableHead>
                  <TableBody>
                    {selected.entries && selected.entries.map((e, idx) => (
                      <TableRow key={e.journalEntry?._id || idx} sx={e.included ? { background: '#e3fcd2' } : {}}>
                        <TableCell><Checkbox checked={!!e.included}
                          onChange={evt => setSelected(sel => {
                            const ents = [...sel.entries]; ents[idx].included = evt.target.checked; return { ...sel, entries: ents };
                          })} /></TableCell>
                        <TableCell>{e.journalEntry ? format(new Date(e.journalEntry.entryDate), 'yyyy-MM-dd') : '-'}</TableCell>
                        <TableCell>{e.journalEntry?.memo || '-'}</TableCell>
                        <TableCell>{e.journalEntry ? formatAmount(e.journalEntry.lines.reduce((a, l) => a + (l.debit || 0), 0)) : ''}</TableCell>
                        <TableCell>{e.journalEntry ? formatAmount(e.journalEntry.lines.reduce((a, l) => a + (l.credit || 0), 0)) : ''}</TableCell>
                        <TableCell>{e.journalEntry?.lines?.length || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </ScrollTable>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpenDlg(false)}>Close</Button>
          <Button variant="contained" color="success" onClick={handleSaveRec}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* Dialog for new rec */}
      <Dialog open={openNew} onClose={()=>setOpenNew(false)}>
        <DialogTitle>Start New Bank Reconciliation</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField select fullWidth margin="normal" label="Account" value={newRec.account} onChange={e=>setNewRec(nr=>({...nr,account:e.target.value}))}>
              <MenuItem value="">Select account</MenuItem>
              {accounts.map(acc=>(
                <MenuItem key={acc._id} value={acc._id}>{acc.code} - {acc.name}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth margin="normal" label="Statement Date" type="date" InputLabelProps={{shrink:true}} value={newRec.statementDate} onChange={e=>setNewRec(nr=>({...nr,statementDate:e.target.value}))} />
            <TextField fullWidth margin="normal" label="Statement Opening Balance" type="number" value={newRec.statementOpeningBalance} onChange={e=>setNewRec(nr=>({...nr,statementOpeningBalance:e.target.value}))} />
            <TextField fullWidth margin="normal" label="Statement Closing Balance" type="number" value={newRec.statementClosingBalance} onChange={e=>setNewRec(nr=>({...nr,statementClosingBalance:e.target.value}))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setOpenNew(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!(newRec.account && newRec.statementDate && newRec.statementOpeningBalance && newRec.statementClosingBalance)}>Create</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BankReconciliation;
